<template>
  <div class="model-picker">
    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"> Model </label>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-2">
      <LoadingSpinner size="sm" />
      <span class="text-sm">Loading models...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="space-y-2">
      <Alert variant="error">
        {{ error }}
      </Alert>
    </div>

    <!-- No Models Loaded State -->
    <div v-else-if="models.length === 0" class="py-2">
      <p class="text-sm text-gray-500 dark:text-gray-400">Click "Start" to load available models</p>
    </div>

    <!-- Model Selection -->
    <div v-else class="space-y-2">
      <Select
        :model-value="modelValue"
        :options="modelOptions"
        placeholder="Select a model"
        @update:model-value="handleModelChange"
      />

      <!-- Model Capabilities -->
      <div v-if="selectedModelDetails" class="flex flex-wrap gap-2 mt-2">
        <Badge v-if="selectedModelDetails.capabilities.text" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Text
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.vision" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
          Vision
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.image" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          Image
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.audio" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
            />
          </svg>
          Audio
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.tts" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
          </svg>
          TTS
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.embeddings" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
            />
          </svg>
          Embeddings
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.video" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          Video
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.moderation" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          Moderation
        </Badge>
        <Badge v-if="selectedModelDetails.capabilities.streaming" variant="secondary" size="sm">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          Streaming
        </Badge>
      </div>

      <!-- Pricing Badge -->
      <div v-if="selectedModelDetails" class="mt-2">
        <PricingBadge
          :pricing="selectedModelDetails.pricing"
          :task="selectedModelDetails.modelTask"
        />
      </div>

      <!-- Model Description -->
      <p
        v-if="selectedModelDetails?.description"
        class="text-xs text-gray-500 dark:text-gray-400 mt-2"
      >
        {{ selectedModelDetails.description }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { Select, Alert, LoadingSpinner, Badge } from '@asabdev/asab-ui';
  import PricingBadge from './PricingBadge.vue';
  import type { Model } from '../../types/models';

  interface Props {
    modelValue: string;
    models: Model[];
    isLoading?: boolean;
    error?: string | null;
  }

  interface Emits {
    (e: 'update:modelValue', value: string): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    isLoading: false,
    error: null,
  });

  const emit = defineEmits<Emits>();

  // Computed
  const modelOptions = computed(() => {
    return props.models.map(model => ({
      value: model.id,
      label: model.name,
    }));
  });

  const selectedModelDetails = computed(() => {
    return props.models.find(model => model.id === props.modelValue);
  });

  // Methods
  const handleModelChange = (value: string) => {
    emit('update:modelValue', value);
  };
</script>

<style scoped>
  .model-picker {
    /* Add any custom styles here */
  }
</style>
